import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSeedListByUniqueId = graphql(/* GraphQL */ `
  query AppliedSeeds(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $uniqueIds: [String!]
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        seeds(first: $first, after: $after, filter: { uniqueIds: $uniqueIds }) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              database
              schema
              meta
              alias
              executionInfo {
                lastRunId
                lastRunStatus
                lastSuccessRunId
                executeCompletedAt
              }
              catalog {
                type
                columns {
                  type
                  name
                  index
                  comment
                  tags
                }
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

type AppliedSeedsByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId: number | null;
  uniqueIds?: string[];
  pause?: boolean;
};

export const useDiscoveryAppliedSeeds = ({
  first,
  after,
  environmentId,
  userId,
  uniqueIds,
  pause,
}: AppliedSeedsByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetSeedListByUniqueId,
      variables: { first, after, environmentId, userId, uniqueIds },
      pause: pause || !environmentId || uniqueIds?.length === 0,
    },
    (data) => {
      const seeds = data.environment.applied?.seeds;
      const pageInfo = seeds?.pageInfo;
      if (seeds == null || pageInfo == null) return undefined;
      return {
        totalCount: seeds.totalCount ?? null,
        endCursor: seeds.pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.applied?.seeds.edges
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.environment.applied?.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
